import { UzTabs } from '@utazzo/ui';
import React from 'react';

import { AppBrandingTab } from './AppBrandingTab';
import { CompanySettingsTab } from './CompanySettingsTab';
import { ScriptsTagsTab } from './scripts/ScriptsTagsTab';
import { canWriteSettingsTab, visibleSettingsTabs } from './settings-tabs';
import { TaxPricingTab } from './TaxPricingTab';
import { WebsiteSettingsTab } from './WebsiteSettingsTab';

import type { SettingsTabId } from '@utazzo/shared';

import { PageShell } from '@/components/page-shell';
import { useMyEffectivePermissions } from '@/features/my-profile/use-my-profile';

/**
 * Settings. One permission module per tab (2026-08-04) - see settings-tabs.ts.
 * A caller holding only `sys.settings.read` reaches this page but sees no tab
 * until an admin grants at least one, which is why the empty state explains
 * itself rather than rendering a bare frame.
 */
export const SettingsPage = (): React.JSX.Element => {
  const permissions = useMyEffectivePermissions();
  const visible = visibleSettingsTabs(permissions);
  const canWrite = (tab: SettingsTabId): boolean => canWriteSettingsTab(permissions, tab);

  const CONTENT: Record<SettingsTabId, { label: string; content: React.ReactNode }> = {
    website: {
      label: 'Website',
      content: <WebsiteSettingsTab canWrite={canWrite('website')} />,
    },
    branding: {
      label: 'Brand & Theme',
      content: <AppBrandingTab canWrite={canWrite('branding')} />,
    },
    tax: { label: 'Tax & Pricing', content: <TaxPricingTab canWrite={canWrite('tax')} /> },
    scripts: {
      label: 'Scripts & tags',
      content: <ScriptsTagsTab canWrite={canWrite('scripts')} />,
    },
    company: {
      label: 'Company / Invoice',
      content: <CompanySettingsTab canWrite={canWrite('company')} />,
    },
  };

  const tabs = visible.map((id) => ({ value: id, ...CONTENT[id] }));

  return (
    <PageShell
      title="Settings"
      description="Configure the public website and the app's branding & theme."
    >
      {tabs.length === 0 ? (
        <p className="rounded-lg border border-dashed border-(--border-strong) p-8 text-center text-sm text-(--fg-faint)">
          {permissions === undefined
            ? 'Loading your settings access…'
            : 'You do not have access to any settings section. Ask an admin to grant a Settings tab permission from Roles.'}
        </p>
      ) : (
        // Phones: the strip scrolls sideways instead of running off-screen.
        <UzTabs
          tabs={tabs}
          listClassName="max-sm:max-w-full max-sm:justify-start max-sm:overflow-x-auto"
        />
      )}
    </PageShell>
  );
};
